// Updated: Complete redesign — modern, minimal layout with inline active URL display and compact history; added expiry countdown label with dot divider and singular/plural day
import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useToastContext } from '../../../contexts/ToastContext';
import { ExternalLink, Clock, Link, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

interface TikkieUrl {
  id: string;
  url: string;
  label: string;
  created_at: string;
  expired_at: string | null;
}

const TIKKIE_EXPIRY_DAYS = 14;

const getDaysUntilExpiry = (createdAt: string): number => {
  const daysSince = Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));
  return TIKKIE_EXPIRY_DAYS - daysSince;
};

const TikkieSection = () => {
  const [history, setHistory] = useState<TikkieUrl[]>([]);
  const [newUrl, setNewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { addToast } = useToastContext();

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('tikkie_urls')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Error fetching tikkie history:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeUrl = history.find(t => t.expired_at === null);
  const expiredUrls = history.filter(t => t.expired_at !== null);

  const handleSave = async () => {
    const trimmedUrl = newUrl.trim();
    if (!trimmedUrl) return;
    setSaving(true);
    try {
      if (activeUrl) {
        const { error: expireError } = await supabase
          .from('tikkie_urls')
          .update({ expired_at: new Date().toISOString() })
          .eq('id', activeUrl.id);
        if (expireError) throw expireError;
      }

      const { error: insertError } = await supabase
        .from('tikkie_urls')
        .insert({ url: trimmedUrl, label: '' });
      if (insertError) throw insertError;

      setNewUrl('');
      addToast('Tikkie URL opgeslagen', 'success');
      await fetchHistory();
    } catch (error) {
      console.error('Error saving tikkie URL:', error);
      addToast('Fout bij het opslaan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-8 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">Standaard Tikkie</h2>
        <p className="text-sm text-gray-500 mt-0.5">Wordt gebruikt als standaard Tikkie link voor events van €75,-.</p>
      </div>

      <div className="p-6 space-y-6">
        {activeUrl && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-green-50 border border-green-200">
            <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
            <a
              href={activeUrl.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-green-800 hover:underline truncate flex items-center gap-1.5 min-w-0 flex-1"
            >
              <span className="truncate">{activeUrl.url}</span>
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            </a>
            <span className="text-xs text-green-600 whitespace-nowrap">{formatDate(activeUrl.created_at)}</span>
            <span className="text-xs text-green-400 whitespace-nowrap">·</span>
            <span className={`text-xs whitespace-nowrap font-medium ${getDaysUntilExpiry(activeUrl.created_at) <= 3 ? 'text-red-600' : 'text-amber-600'}`}>
              {getDaysUntilExpiry(activeUrl.created_at) > 0
                ? `Verloopt over ${getDaysUntilExpiry(activeUrl.created_at)} ${getDaysUntilExpiry(activeUrl.created_at) === 1 ? 'dag' : 'dagen'}`
                : 'Verlopen'}
            </span>
          </div>
        )}

        {!activeUrl && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="w-2 h-2 rounded-full bg-gray-300 shrink-0" />
            <span className="text-sm text-gray-500">Geen actieve Tikkie URL</span>
          </div>
        )}

        <div className="flex gap-3">
          <div className="relative flex-1">
            <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://tikkie.me/..."
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#E37C64]/20 focus:border-[#E37C64] transition-colors"
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !newUrl.trim()}
            className="px-5 py-2.5 bg-[#E37C64] hover:bg-[#5A130E] text-white text-sm font-medium rounded-lg disabled:opacity-40 transition-colors whitespace-nowrap"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              activeUrl ? 'Vervangen' : 'Opslaan'
            )}
          </button>
        </div>
      </div>

      {expiredUrls.length > 0 && (
        <div className="border-t border-gray-100">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between px-6 py-3 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Geschiedenis ({expiredUrls.length})
            </span>
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showHistory && (
            <div className="px-6 pb-4 space-y-1">
              {expiredUrls.map(t => (
                <div
                  key={t.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors group"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-500 hover:text-gray-700 hover:underline truncate min-w-0 flex-1"
                  >
                    {t.url}
                  </a>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {formatDate(t.created_at)} — {formatDate(t.expired_at!)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TikkieSection;
